export enum SubscriptionStatus {
  ACTIVE = 'active',
  PAST_DUE = 'past_due',
  CANCELED = 'canceled',
  INCOMPLETE = 'incomplete',
  TRIALING = 'trialing',
  UNPAID = 'unpaid',
}

export interface Subscription {
  uuid: string;
  userUuid: string;
  status: SubscriptionStatus;
  currentPeriodStart: string; // ISO 8601
  currentPeriodEnd: string; // ISO 8601
  cancelAtPeriodEnd: boolean;
  trialEnd: string | null;
  autoRenewal: boolean;
  createdAt: string; // ISO 8601
  updatedAt: string; // ISO 8601
}

export interface User {
  id: string;
  uuid: string;
  email: string;
  role: string;
  firstName: string;
  lastName: string;
  preferredLanguage: string;
  timezone?: string;
  dateFormat?: string;
  emailNotifications: boolean;
  dsnEmailRejected?: boolean;
  dsnEmailNotGenerated?: boolean;
  dsnEmailPendingTooLong?: boolean;
  pushNotifications: boolean;
  lastLoginAt: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  subscription: Subscription | undefined;
  twoFactorEnabled: boolean;
  isTwoFactorEnabled: boolean; // Alias from backend
  securityMethod: 'totp' | 'otp' | null;
  // Vault user fields
  requires2FASetup?: boolean;
  vaultOnlyAccess?: boolean;
  pendingVaultId?: string | null;
}

export interface UserPreferences {
  preferredLanguage: string;
  emailNotifications: boolean;
  dsnEmailRejected?: boolean;
  dsnEmailNotGenerated?: boolean;
  dsnEmailPendingTooLong?: boolean;
  pushNotifications: boolean;
  timezone?: string;
  dateFormat?: string;
}

export interface UpdatePreferencesData {
  preferredLanguage?: string;
  emailNotifications?: boolean;
  dsnEmailRejected?: boolean;
  dsnEmailNotGenerated?: boolean;
  dsnEmailPendingTooLong?: boolean;
  pushNotifications?: boolean;
  timezone?: string;
  dateFormat?: string;
}

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
}

export interface AuthResponse {
  user: User;
  tokens: TokenPair;
  requires2fa?: boolean;
  sessionId?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  openPayeUsername: string;
  openPayePassword: string;
  companyName: string;
  preferredLanguage: string;
  timezone?: string;
  acceptTerms: boolean | undefined;
}

export interface ForgotPasswordData {
  email: string;
}

export interface ResetPasswordData {
  token: string;
  newPassword: string;
  confirmPassword: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  meta: {
    timestamp: string;
    path: string;
    statusCode: number;
  };
}

// ===== 2FA Types =====

export interface TwoFactorSetupResponse {
  qrCode: string;
  secret: string;
  message: string;
}

export interface TwoFactorVerifyData {
  token: string;
  tempToken?: string;
}

export interface TwoFactorLoginResponse {
  requiresTwoFactor: boolean;
  tempToken?: string;
  message: string;
}

export interface OtpLoginResponse {
  requiresOtp: boolean;
  sessionId: string;
}

export interface TwoFactorDisableData {
  password: string;
}

// ===== Invitation Registration Types =====

export interface RegisterFromInvitationData {
  invitationToken: string;
  email: string;
  password: string;
  confirmPassword: string;
  firstName?: string;
  lastName?: string;
  preferredLanguage?: string;
  timezone?: string;
}

export interface RegisterFromInvitationResponse extends AuthResponse {
  vaultId: string;
  requires2FASetup: boolean;
  vaultInitializationPending: boolean;
}

// ===== Vault User Types =====

export interface Check2FAStatusResponse {
  requires2FASetup: boolean;
  twoFactorEnabled: boolean;
}

export interface Complete2FASetupResponse {
  message: string;
}
